import { Suspense } from 'react'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  type RouterHistory,
  Navigate,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router'
import type { AuthContextValue } from '@/features/auth/AuthProvider'
import type { ProfileContextValue } from '@/features/profile/ProfileProvider'
import { homePath } from '@/features/profile/permissions'
import { ContactPage as MyContactPage } from '@/features/contacts/pages/ContactPage/ContactPage'
import { MyContactsPage } from '@/features/contacts/pages/MyContactsPage/MyContactsPage'
import { isPublicTicketPath, isSignedOutPath } from '@/app/publicPages/publicPages'
import { AppShell } from '@/app/layout/AppShell/AppShell'
import { LoadingScreen } from '@/components/LoadingScreen/LoadingScreen'
import { PwaUpdatePrompt } from '@/components/PwaUpdatePrompt/PwaUpdatePrompt'
import type { EventHubSection } from '@/features/events/pages/EventDetailPage/EventDetailPage'
import type { SessionTab } from '@/features/sessions/pages/SessionPage/SessionPage'

// Every screen is code-split from the shell. The router only fetches the matched
// route's chunk, so a first load downloads the app chrome plus the one page it is
// about to show instead of all seventeen at once.
const OnboardingPage = lazyRouteComponent(() => import('@/features/profile/pages/OnboardingPage/OnboardingPage'), 'OnboardingPage')
const ForgotPasswordPage = lazyRouteComponent(() => import('@/features/auth/pages/ForgotPasswordPage/ForgotPasswordPage'), 'ForgotPasswordPage')
const LoginPage = lazyRouteComponent(() => import('@/features/auth/pages/LoginPage/LoginPage'), 'LoginPage')
const ResetPasswordPage = lazyRouteComponent(() => import('@/features/auth/pages/ResetPasswordPage/ResetPasswordPage'), 'ResetPasswordPage')
const SignupPage = lazyRouteComponent(() => import('@/features/auth/pages/SignupPage/SignupPage'), 'SignupPage')
const HelpPage = lazyRouteComponent(() => import('@/app/pages/HelpPage/HelpPage'), 'HelpPage')
const RoadmapPage = lazyRouteComponent(() => import('@/app/pages/RoadmapPage/RoadmapPage'), 'RoadmapPage')
const RoadmapMilestonePage = lazyRouteComponent(() => import('@/app/pages/RoadmapMilestonePage/RoadmapMilestonePage'), 'RoadmapMilestonePage')
const CheckInPage = lazyRouteComponent(() => import('@/features/check-in/pages/CheckInPage/CheckInPage'), 'CheckInPage')
const CreateEventPage = lazyRouteComponent(() => import('@/features/events/pages/CreateEventPage/CreateEventPage'), 'CreateEventPage')
const EventDetailPage = lazyRouteComponent(() => import('@/features/events/pages/EventDetailPage/EventDetailPage'), 'EventDetailPage')
const EventListPage = lazyRouteComponent(() => import('@/features/events/pages/EventListPage/EventListPage'), 'EventListPage')
const JoinSessionPage = lazyRouteComponent(() => import('@/features/sessions/pages/JoinSessionPage/JoinSessionPage'), 'JoinSessionPage')
const SessionsLandingPage = lazyRouteComponent(() => import('@/features/sessions/pages/SessionsLandingPage/SessionsLandingPage'), 'SessionsLandingPage')
const SessionPage = lazyRouteComponent(() => import('@/features/sessions/pages/SessionPage/SessionPage'), 'SessionPage')
const LiveViewPage = lazyRouteComponent(() => import('@/features/sessions/pages/LiveViewPage/LiveViewPage'), 'LiveViewPage')
const SessionRegistrationsPage = lazyRouteComponent(() => import('@/features/sessions/pages/SessionRegistrationsPage/SessionRegistrationsPage'), 'SessionRegistrationsPage')
const ContactPage = lazyRouteComponent(() => import('@/features/reports/pages/ContactPage/ContactPage'), 'ContactPage')
const ReportsPage = lazyRouteComponent(() => import('@/features/reports/pages/ReportsPage/ReportsPage'), 'ReportsPage')
const SettingsPage = lazyRouteComponent(() => import('@/app/pages/SettingsPage/SettingsPage'), 'SettingsPage')
const TeamPage = lazyRouteComponent(() => import('@/features/team/pages/TeamPage/TeamPage'), 'TeamPage')
const PublicTicketPage = lazyRouteComponent(() => import('@/features/tickets/pages/PublicTicketPage/PublicTicketPage'), 'PublicTicketPage')

export type RouterContext = Readonly<{ auth: AuthContextValue; profile: ProfileContextValue }>

function RootLayout() {
  // Attendees on a public ticket page get no service worker and no "new
  // version" banner: that is the staff app's, not theirs.
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const publicPage = isPublicTicketPath(pathname)
  return (
    <>
      <Suspense fallback={<LoadingScreen />}>
        <Outlet />
      </Suspense>
      {/* Outside Suspense, so it stays visible while a screen's code loads. */}
      {/* Before signing in it stays quiet: still checking, not yet asking. */}
      {publicPage ? null : <PwaUpdatePrompt quiet={isSignedOutPath(pathname)} />}
    </>
  )
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: RootLayout })

type JoinCodeSearch = Readonly<{ joinCode?: string }>
const joinCodeSearch = (search: Record<string, unknown>): JoinCodeSearch => ({
  ...(typeof search.joinCode === 'string' && /^\d{4}$/.test(search.joinCode) ? { joinCode: search.joinCode } : {}),
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: joinCodeSearch,
  component: LoginPage,
})
const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  validateSearch: joinCodeSearch,
  component: SignupPage,
})
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
})
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
})
const roadmapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/roadmap',
  component: RoadmapPage,
})
const roadmapMilestoneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/roadmap/$milestoneSlug',
  component: () => {
    const { milestoneSlug } = roadmapMilestoneRoute.useParams()
    return <RoadmapMilestonePage milestoneSlug={milestoneSlug} />
  },
})
const publicTicketRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ticket/$eventLocator/$token',
  component: () => {
    const { eventLocator, token } = publicTicketRoute.useParams()
    return <PublicTicketPage eventLocator={eventLocator} token={token} />
  },
})
// The short form of a ticket link, /t/<event code>/<secret>.
const shortTicketRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/t/$eventCode/$token',
  component: () => {
    const { eventCode, token } = shortTicketRoute.useParams()
    return <PublicTicketPage eventLocator={eventCode} token={token} />
  },
})
const onboardingRoute = createRoute({ getParentRoute: () => rootRoute, path: '/onboarding', validateSearch: joinCodeSearch, component: OnboardingPage })
const joinSessionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/join',
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.code === 'string' && /^\d{4}$/.test(search.code) ? { code: search.code } : {}),
  }),
  component: () => {
    const { code } = joinSessionRoute.useSearch()
    return <JoinSessionPage {...(code ? { code } : {})} />
  },
})

function ProtectedLayout() {
  const { auth, profile } = protectedRoute.useRouteContext()
  if (auth.loading) return <LoadingScreen />
  if (!auth.session) return <Navigate to="/login" replace />
  // The profile is reconciled in the background instead of holding every data
  // fetch behind a spinner: while it is in flight the shell renders straight away
  // and the route's own loaders run alongside it.
  //
  // The onboarding redirect must therefore wait for `profile.loading` to settle.
  // While the profile is unknown it is `null`, and `!null?.onboardingCompletedAt`
  // is true — dropping the check entirely would bounce every user to /onboarding
  // on every load. `loading` tells "not known yet" apart from "genuinely absent".
  if (!profile.loading && !profile.profile?.onboardingCompletedAt) return <Navigate to="/onboarding" replace />
  return <AppShell />
}

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authenticated',
  beforeLoad: ({ context }) => {
    // TanStack Router intentionally throws its redirect control object.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    if (!context.auth.loading && !context.auth.session) throw redirect({ to: '/login' })
  },
  component: ProtectedLayout,
})

const homeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: function Home() {
    // Everyone opens on the first tab they can use.
    const { profile } = homeRoute.useRouteContext()
    // The shell is already up; the role arrives with the profile.
    if (!profile.profile) return null
    // Unknown permissions come from a profile cached before they existed:
    // that account had every tab, so Events is still the right start.
    return <Navigate to={profile.profile.permissions ? homePath(profile.profile.permissions) : '/events'} replace />
  },
})

const eventsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/events',
  component: EventListPage,
})
const sessionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sessions',
  component: SessionsLandingPage,
})
const sessionRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sessions/$sessionId',
  // A tapped notification opens ?tab=updates&broadcast=<id>.
  validateSearch: (search: Record<string, unknown>): Readonly<{ tab?: SessionTab; broadcast?: string }> => ({
    ...(search.tab === 'mine' || search.tab === 'updates' ? { tab: search.tab } : {}),
    ...(search.tab === 'updates' && typeof search.broadcast === 'string' && search.broadcast ? { broadcast: search.broadcast } : {}),
  }),
  component: () => {
    const { sessionId } = sessionRoute.useParams()
    const { tab, broadcast } = sessionRoute.useSearch()
    return <SessionPage sessionId={sessionId} tab={tab ?? 'log'} highlight={broadcast ?? null} />
  },
})
// Live View belongs to the event it's opened from, so it sits in the Events
// area: the Events tab stays highlighted and the Outreach tab's remembered
// place is never replaced by it.
const liveViewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/events/$eventId/sessions/$sessionId/live',
  component: () => {
    const { eventId, sessionId } = liveViewRoute.useParams()
    // Keyed by session, so another session's figures never show under this address.
    return <LiveViewPage key={sessionId} eventId={eventId} sessionId={sessionId} />
  },
})
// A session's registrations sit beside Live View, in the Events area.
const sessionRegistrationsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/events/$eventId/sessions/$sessionId/registrations',
  component: () => {
    const { eventId, sessionId } = sessionRegistrationsRoute.useParams()
    // Keyed by session: moving between sessions (back and forward) starts afresh
    // rather than showing the previous session's people under the new address.
    return <SessionRegistrationsPage key={sessionId} eventId={eventId} sessionId={sessionId} />
  },
})
// The registration form used to have its own address; it is the Log tab now.
const registerAttendeeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/sessions/$sessionId/register',
  beforeLoad: ({ params }) => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/sessions/$sessionId', params, replace: true })
  },
})
// My contacts: everyone you've registered, at every event (Outreach area).
const myContactsRoute = createRoute({ getParentRoute: () => protectedRoute, path: '/contacts', component: () => <MyContactsPage /> })
const contactRouteForPerson = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/contacts/$contactId',
  component: () => {
    const { contactId } = contactRouteForPerson.useParams()
    return <MyContactPage contactId={contactId} />
  },
})
const checkInRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/check-in',
  component: CheckInPage,
})
const reportsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports',
  // "contacts" was this tab's old name: links and remembered tabs still land on Export.
  validateSearch: (search: Record<string, unknown>): Readonly<{ tab?: 'export'; event?: string }> => ({
    ...(search.tab === 'export' || search.tab === 'contacts' ? { tab: 'export' as const } : {}),
    ...(typeof search.event === 'string' && search.event ? { event: search.event } : {}),
  }),
  component: () => {
    const { tab, event } = reportsRoute.useSearch()
    return <ReportsPage tab={tab ?? 'dashboard'} eventId={event} />
  },
})
const contactRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/contacts/$contactId',
  component: () => <ContactPage contactId={contactRoute.useParams().contactId} />,
})
const teamRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/team',
  component: TeamPage,
})
// Settings, opened from ☰ in the app bar: your account, admin, support.
const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings',
  component: SettingsPage,
})
const helpRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/help',
  validateSearch: (search: Record<string, unknown>): { topic?: string } => (typeof search.topic === 'string' ? { topic: search.topic } : {}),
  component: () => {
    const { topic } = helpRoute.useSearch()
    return <HelpPage topic={topic} />
  },
})
const createEventRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/events/new',
  component: CreateEventPage,
})
type HubSearch = Readonly<{ edit?: boolean; section?: EventHubSection }>

const eventDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/events/$eventId',
  validateSearch: (search: Record<string, unknown>): HubSearch => ({
    ...(search.edit === true || search.edit === 'true' ? { edit: true } : {}),
    ...(search.section === 'details' || search.section === 'form' || search.section === 'sessions'
      ? { section: search.section }
      : {}),
  }),
  component: () => {
    const { eventId } = eventDetailRoute.useParams()
    const { edit, section } = eventDetailRoute.useSearch()
    return (
      <EventDetailPage
        eventId={eventId}
        section={section ?? 'details'}
        {...(edit ? { edit } : {})}
      />
    )
  },
})

// C2 aliases: the historical paths still resolve, landing on the hub with the
// matching section selected so existing links and e2e journeys keep working.
const eventSessionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/events/$eventId/sessions',
  beforeLoad: ({ params }) => {
    // TanStack Router intentionally throws its redirect control object.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: '/events/$eventId',
      params,
      search: { section: 'sessions' },
      replace: true,
    })
  },
})
const editEventRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/events/$eventId/edit',
  beforeLoad: ({ params }) => {
    // An old edit link still means "edit this event", so the alias lands on the
    // Details section with its editor open.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: '/events/$eventId',
      params,
      search: { section: 'details', edit: true },
      replace: true,
    })
  },
})
const formRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/events/$eventId/form',
  beforeLoad: ({ params }) => {
    // The standalone full-screen preview page is gone, so an old link lands on
    // the Form section rather than a dead end.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: '/events/$eventId',
      params,
      search: { section: 'form' },
      replace: true,
    })
  },
})
const formEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/events/$eventId/form/edit',
  beforeLoad: ({ params }) => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: '/events/$eventId',
      params,
      search: { section: 'form' },
      replace: true,
    })
  },
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  signupRoute,
  joinSessionRoute,
  forgotPasswordRoute,
  resetPasswordRoute, onboardingRoute,
  roadmapRoute,
  roadmapMilestoneRoute,
  publicTicketRoute,
  shortTicketRoute,
  protectedRoute.addChildren([homeRoute, eventsRoute, sessionsRoute, sessionRoute, liveViewRoute,
  sessionRegistrationsRoute, registerAttendeeRoute, myContactsRoute, contactRouteForPerson, checkInRoute, reportsRoute, contactRoute, teamRoute, settingsRoute, helpRoute, createEventRoute, eventDetailRoute, eventSessionsRoute, editEventRoute, formRoute, formEditRoute]),
])

export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    context: { auth: undefined!, profile: undefined! },
    defaultPreload: 'intent',
    ...(history ? { history } : {}),
  })
}

export const router = createAppRouter()

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

declare module '@tanstack/history' {
  interface HistoryState {
    /** The address a screen (Settings, or one opened from it) was opened from, for its Back button. */
    openedFrom?: string
  }
}
